import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import DataGrid from './DataGrid';
import { api } from '../lib/api';
import { useLocale } from '../lib/LocaleContext';
import styles from './KnowledgeBase.module.css';

function formatDate(s) {
  try { return new Date(s).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return s || '-'; }
}

function truncate(str, max = 50) {
  if (str == null) return '-';
  const s = typeof str === 'string' ? str : JSON.stringify(str);
  return s.length <= max ? s : s.slice(0, max) + '...';
}

function val(row, key) {
  const k = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return row?.[key] ?? row?.[k] ?? row?.[key.charAt(0).toUpperCase() + key.slice(1)] ?? '-';
}

export default function KnowledgeBase({ embedded = false, industryCode: industryCodeProp }) {
  const [searchParams] = useSearchParams();
  const { t } = useLocale();
  const industryCode = industryCodeProp || searchParams.get('industry') || 'ev';

  const [topRatio, setTopRatio] = useState(70);
  const [leftRatio, setLeftRatio] = useState(60);
  const [dragH, setDragH] = useState(false);
  const [dragV, setDragV] = useState(false);
  const containerRef = useRef(null);
  const topInnerRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [usageLogsForDoc, setUsageLogsForDoc] = useState([]);
  const [loadingLogsForDoc, setLoadingLogsForDoc] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [loadingAllLogs, setLoadingAllLogs] = useState(false);

  const fetchAllLogs = useCallback(async () => {
    setLoadingAllLogs(true);
    try {
      const params = { limit: 300 };
      if (industryCode) params.industry = industryCode;
      const res = await api.documentUsageLogs(params);
      setAllLogs(res.data || []);
    } catch (e) { console.error(e); setAllLogs([]); }
    finally { setLoadingAllLogs(false); }
  }, [industryCode]);

  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await api.documents({ industry: industryCode, limit: 500 });
      const list = (res.data || []).map(d => ({
        id: d?.id ?? d?.Id,
        content: d?.content ?? d?.Content ?? '',
        metadata: d?.metadata ?? d?.Metadata
      })).filter(d => d.id != null);
      setDocuments(list);
    } catch (e) { console.error(e); setDocuments([]); }
    finally { setLoadingDocs(false); }
  }, [industryCode]);

  useEffect(() => {
    if (!industryCode) return;
    fetchDocuments();
  }, [industryCode, fetchDocuments]);

  const fetchUsageLogsForDocument = useCallback(async (documentId) => {
    if (!documentId) { setUsageLogsForDoc([]); return; }
    setLoadingLogsForDoc(true);
    try {
      const res = await api.documentUsageLogs({ document_id: documentId, limit: 200 });
      setUsageLogsForDoc(res.data || []);
    } catch (e) { console.error(e); setUsageLogsForDoc([]); }
    finally { setLoadingLogsForDoc(false); }
  }, []);

  useEffect(() => {
    if (!selectedDoc) { setUsageLogsForDoc([]); return; }
    fetchUsageLogsForDocument(selectedDoc.id);
  }, [selectedDoc, fetchUsageLogsForDocument]);

  useEffect(() => {
    if (!industryCode) return;
    fetchAllLogs();
  }, [industryCode, fetchAllLogs]);

  useEffect(() => {
    if (!dragH) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const pct = Math.max(20, Math.min(80, (y / rect.height) * 100));
      setTopRatio(pct);
    };
    const onUp = () => setDragH(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'row-resize';
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; };
  }, [dragH]);

  useEffect(() => {
    if (!dragV) return;
    const onMove = (e) => {
      if (!topInnerRef.current) return;
      const rect = topInnerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(30, Math.min(70, (x / rect.width) * 100));
      setLeftRatio(pct);
    };
    const onUp = () => setDragV(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; };
  }, [dragV]);

  const docColumns = [
    { key: 'id', label: 'ID', width: '72px', render: v => <span className={styles.cellId}>{v}</span> },
    { key: 'content', label: t.content, render: v => <span className={styles.cellContent} title={typeof v === 'string' ? v : undefined}>{truncate(v, 50)}</span> }
  ];

  /* 右半部分：仅 客户问题、相似度、时间 */
  const logColumnsRight = [
    { key: 'query_text', label: t.customerQuestion, width: '42%', render: (v, row) => (row?.query_text ?? row?.QueryText ?? '-') },
    { key: 'similarity', label: t.similarity, width: '100px', render: (v, row) => { const n = row?.similarity ?? row?.Similarity; return n != null ? Number(n).toFixed(4) : '-'; } },
    { key: 'created_at', label: t.time, width: '160px', render: (v, row) => formatDate(row?.created_at ?? row?.CreatedAt) }
  ];

  /* 下半区域：日志表全表核心字段 */
  const logColumnsFull = [
    { key: 'id', label: 'id', width: '64px', render: (v, row) => val(row, 'id') },
    { key: 'document_id', label: 'document_id', width: '90px', render: (v, row) => val(row, 'document_id') },
    { key: 'industry_id', label: 'industry_id', width: '90px', render: (v, row) => val(row, 'industry_id') },
    { key: 'message_id', label: 'message_id', width: '120px', render: (v, row) => truncate(val(row, 'message_id'), 40) },
    { key: 'customer_id', label: 'customer_id', width: '120px', render: (v, row) => truncate(val(row, 'customer_id'), 40) },
    { key: 'session_id', label: 'session_id', width: '120px', render: (v, row) => truncate(val(row, 'session_id'), 40) },
    { key: 'similarity', label: t.similarity, width: '88px', render: (v, row) => { const n = row?.similarity ?? row?.Similarity; return n != null ? Number(n).toFixed(4) : '-'; } },
    { key: 'query_text', label: t.customerQuestion, width: '200px', render: (v, row) => truncate(val(row, 'query_text'), 80) },
    { key: 'usage_context', label: 'usage_context', width: '140px', render: (v, row) => { const u = row?.usage_context ?? row?.UsageContext; return u != null ? truncate(typeof u === 'string' ? u : JSON.stringify(u), 50) : '-'; } },
    { key: 'created_at', label: 'created_at', width: '160px', render: (v, row) => formatDate(val(row, 'created_at')) }
  ];

  return (
    <div className={`${styles.page} ${embedded ? styles.embeddedPage : ''}`} ref={containerRef}>
      {!embedded && (
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>{t.kbTitle.replace('{code}', industryCode)}</h1>
          <Link className={styles.backLink} to="/">{t.backToHome}</Link>
        </header>
      )}

      <div className={styles.topSection} style={{ flex: `0 0 ${topRatio}%` }}>
        <div className={styles.topInner} ref={topInnerRef}>
          <div className={styles.leftPanel} style={{ flex: `0 0 ${leftRatio}%` }}>
            <DataGrid
              className={styles.kbGrid}
              title={t.kbRecords}
              columns={docColumns}
              data={documents}
              loading={loadingDocs}
              onRefresh={fetchDocuments}
              onRowClick={(row) => setSelectedDoc({ id: row.id, content: row.content, metadata: row.metadata })}
            />
          </div>
          <div className={styles.vResizeBar} onMouseDown={(e) => { e.preventDefault(); setDragV(true); }}>
            <div className={styles.vResizeHandle} />
          </div>
          <div className={styles.rightPanel} style={{ flex: `0 0 ${100 - leftRatio}%` }}>
            <DataGrid
              className={styles.kbGrid}
              title={t.usageLogForRecord}
              columns={logColumnsRight}
              data={selectedDoc ? usageLogsForDoc : []}
              loading={!!selectedDoc && loadingLogsForDoc}
              emptyText={!selectedDoc ? t.selectRecordHint : t.noUsageRecords}
            />
          </div>
        </div>
      </div>

      <div className={styles.hResizeBar} onMouseDown={(e) => { e.preventDefault(); setDragH(true); }}>
        <div className={styles.hResizeHandle} />
      </div>

      <div className={styles.bottomSection} style={{ flex: `0 0 ${100 - topRatio}%` }}>
        <DataGrid
          className={styles.kbGrid}
          title={t.allUsageLogs}
          columns={logColumnsFull}
          data={allLogs}
          loading={loadingAllLogs}
          onRefresh={fetchAllLogs}
        />
      </div>
    </div>
  );
}
